import React, {useState,useEffect} from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import CartBadge from "./CartBadge";
import HomeIcon from "../assets/images/icons/Category.svg";
import CartIcon from "../assets/images/icons/Buy.svg";
import UserIcon from "../assets/images/icons/Profile.svg";
import EnvelopeIcon from "../assets/images/icons/Chat.svg";

const BottomMenu = ({ selectedFridgeId }) => {
    const { cartItems } = useCart();
    const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
    const navigate = useNavigate();
    const location = useLocation();

    const [showAddedBadge, setShowAddedBadge] = useState(false);
    const [addedQuantity, setAddedQuantity] = useState(0);
    const [pendingCartUpdate, setPendingCartUpdate] = useState(false);

    useEffect(() => {
        const handleProductAdded = (event) => {
            const quantity = event.detail.quantity || 1;
            setAddedQuantity(quantity);
            setShowAddedBadge(true);
            setPendingCartUpdate(true);

            // Ta bort +X badge efter 1.5s
            setTimeout(() => {
                setShowAddedBadge(false);
                // Vänta lite till innan vi visar den röda badge:n
                setTimeout(() => {
                    setPendingCartUpdate(false);
                }, 300);
            }, 1500);
        };

        window.addEventListener('productAddedToCart', handleProductAdded);
        return () => {
            window.removeEventListener('productAddedToCart', handleProductAdded);
        };
    }, []);

    // Bestäm vilken sida som är aktiv
    const isActive = (path) => {
        if (path === "/products") {
            return location.pathname === "/" || location.pathname === "/products";
        }
        return location.pathname === path;
    };

    // Treat both cart routes as active for the Cart tab
    const isCartActive = location.pathname === "/cart" || location.pathname === "/cart-voucher";

    // Get the fridge id and voucher flag from storage/props
    const fridgeIdForNav = selectedFridgeId ?? localStorage.getItem("selectedFridgeId") ?? null;

    return (
        <Container>
            <MenuItem
                onClick={() => navigate("/products")}
                $active={isActive("/products")}
            >
                <IconContainer $active={isActive("/products")}>
                    <SVGIcon src={HomeIcon} alt="Home" $active={isActive("/products")} />
                </IconContainer>
                <MenuText $active={isActive("/products")}>
                    Butik
                </MenuText>
                {isActive("/products") && <ActiveIndicator />}
            </MenuItem>
            <MenuItem
                onClick={() => {
                    // Read fresh each click to avoid stale value
                    const raw = localStorage.getItem("isVoucherFridge");
                    let goVoucher = false;
                    try { goVoucher = JSON.parse(raw ?? "false"); } catch { goVoucher = raw === "true"; }
                    const target = goVoucher ? "/cart-voucher" : "/cart";
                    console.log("BottomMenu: Cart click", { selectedFridgeId: fridgeIdForNav, isVoucherFridgeRaw: raw, isVoucherFridge: goVoucher, target });
                    navigate(target, {
                        state: { selectedFridgeId: fridgeIdForNav },
                    });
                }}
                $active={isCartActive}
            >
                <IconContainer $active={isCartActive}>
                    <SVGIcon src={CartIcon} alt="Cart" $active={isCartActive} />
                    {cartCount > 0 && !pendingCartUpdate && <CartBadge count={cartCount} />}
                    {showAddedBadge && <AddedBadge>+{addedQuantity}</AddedBadge>}
                </IconContainer>
                <MenuText $active={isCartActive}>Kassa</MenuText>
                {isCartActive && <ActiveIndicator />}
            </MenuItem>
            <MenuItem
                onClick={() => navigate("/contact")}
                $active={isActive("/contact")}
            >
                <IconContainer $active={isActive("/contact")}>
                    <SVGIcon src={EnvelopeIcon} alt="Contact" $active={isActive("/contact")} />
                </IconContainer>
                <MenuText $active={isActive("/contact")}>Kontakt</MenuText>
                {isActive("/contact") && <ActiveIndicator />}
            </MenuItem>
            <MenuItem
                onClick={() => navigate("/account")}
                $active={isActive("/account")}
            >
                <IconContainer $active={isActive("/account")}>
                    <SVGIcon src={UserIcon} alt="User" $active={isActive("/account")} />
                </IconContainer>
                <MenuText $active={isActive("/account")}>
                    Konto
                </MenuText>
                {isActive("/account") && <ActiveIndicator />}
            </MenuItem>
        </Container>
    );
};

export default BottomMenu;


const slideUp = keyframes`
    0% {
        transform: translateY(20px);
        opacity: 0;
    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
`;

const fadeOut = keyframes`
    0% {
        opacity: 1;
        transform: scale(1);
    }
    100% {
        opacity: 0;
        transform: scale(0.8);
    }
`;

const Container = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    display: flex;
    justify-content: space-around;
    align-items: center;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(12px);
    border-top: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 25px 25px 0 0;
    padding: 8px 0 max(8px, env(safe-area-inset-bottom));
    z-index: 1000;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
    height: 68px;
`;

const MenuItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 8px;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    transform: ${({ $active }) => ($active ? "scale(1.05) translateY(-1px)" : "scale(1)")};
    background: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.4)" : "transparent")};

    &:hover {
        background: rgba(255, 255, 255, 0.6);
        transform: ${({ $active }) => ($active ? "scale(1.05) translateY(-1px)" : "scale(1.02) translateY(-0.5px)")};
    }

    &:active {
        transform: ${({ $active }) => ($active ? "scale(1.03) translateY(-0.5px)" : "scale(0.98)")};
    }
`;

const IconContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    transition: all 0.3s ease;
    transform: ${({ $active }) => ($active ? "scale(1.05)" : "scale(1)")};
`;

const SVGIcon = styled.img`
    width: 18px;
    height: 18px;
    opacity: ${({ $active }) => ($active ? "1" : "0.7")};
    transition: all 0.3s ease;
    filter: ${({ $active }) => ($active ? "brightness(0.3)" : "brightness(0.6)")};
    
    ${MenuItem}:hover & {
        opacity: 1;
        transform: scale(1.05);
        filter: brightness(0.3);
    }
`;

const MenuText = styled.span`
    font-size: 10px;
    color: ${({ $active }) => ($active ? "#1f2937" : "#6b7280")};
    font-weight: ${({ $active }) => ($active ? "600" : "500")};
    opacity: ${({ $active }) => ($active ? "1" : "0.8")};
    transition: all 0.3s ease;
    
    ${MenuItem}:hover & {
        opacity: 1;
        color: #1f2937;
    }
`;

const ActiveIndicator = styled.div`
    position: absolute;
    top: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background: #374151;
    border-radius: 50%;
    animation: pulse 2s infinite;
    
    @keyframes pulse {
        0%, 100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
        }
        50% {
            opacity: 0.7;
            transform: translateX(-50%) scale(1.2);
        }
    }
`;

const AddedBadge = styled.div`
    position: absolute;
    top: -8px;
    right: -8px;
    background: rgba(37, 37, 37, 0.9);
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    animation: ${slideUp} 0.3s ease-out, ${fadeOut} 0.3s ease-out 1.2s forwards;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(37, 37, 37, 0.4);
`;